// An archive read as a wall of photographs.
//
// The archive is never unpacked. What arrives here is a page of thumbnails with minted urls, and
// clicking one streams that single picture out of the zip at full size — so a 2 GB archive of site
// photos opens as fast as a small one, and nothing is copied anywhere to make it happen.
//
// The loader is a prop because there are two doors to the same index: /documents authorizes through the
// drive's permissions, Project Management through a project's. This component is told how to ask.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { formatBytes } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { Button } from '/imports/ui/Button';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { DriveGalleryItem, DriveGalleryPage } from '/imports/api/drive';

const PAGE = 200;
/** How often a still-indexing archive is asked again. Long enough not to hammer, short enough to feel live. */
const POLL_MS = 4000;

const DriveGalleryModalStyled = styled(ModalStyled)`
  /* A gallery earns the width: more columns is the whole point, and a wall of photographs in a narrow
     column reads no better than the list of filenames it replaced. min() rather than a bare min-width,
     because min-width beats max-width in the cascade, so 80rem alone would overflow a phone. */
  .modal-window { min-width: min(80rem, 100%); max-width: 100%; }

  .gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
    gap: 0.5rem;
    max-height: 68vh;
    overflow: auto;
    padding: 0.15rem;
  }
  .gallery .shot {
    border: 1px solid ${css.grey7}; border-radius: ${css.inputBorderRadius}; overflow: hidden;
    background: ${css.grey9}; cursor: pointer; padding: 0;
  }
  .gallery .shot:hover { border-color: ${css.grey5}; }
  .gallery .shot:focus-visible { outline: 2px solid ${css.primaryColor}; outline-offset: 1px; }
  /* 4:3 because that is what a phone and a site camera both produce; a square crop would cut the thing
     the photograph was taken to show. */
  .gallery .shot .art {
    aspect-ratio: 4 / 3; display: flex; align-items: center; justify-content: center; width: 100%;
  }
  .gallery .shot img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .gallery .shot .cap {
    display: block; font-size: 0.7rem; color: ${css.grey3}; padding: 0.2rem 0.3rem;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left;
  }

  .gallery-foot {
    display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
    padding-top: 0.6rem; font-size: 0.8rem; color: ${css.grey4};
  }
  .gallery-foot .spacer { margin-left: auto; }
  .gallery-note { padding: 1.5rem 0.5rem; color: ${css.grey4}; }
`;

// The lightbox is outside the modal's own box: it covers the window, because a photograph being
// examined for detail should not be inset inside a dialog that is already inset in the page.
const LightboxStyled = styled.div`
  position: fixed; inset: 0; z-index: 40;
  background: rgba(0, 0, 0, 0.92);
  display: flex; flex-direction: column;

  .shot-area { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; }
  .shot-area img { max-width: 100%; max-height: 100%; object-fit: contain; }
  .bar {
    display: flex; align-items: center; gap: 0.75rem; padding: 0.5rem 0.75rem;
    color: ${css.white}; font-size: 0.8rem;
  }
  .bar .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .bar .spacer { margin-left: auto; }
  .bar a { color: ${css.white}; }
  .nav {
    background: rgba(255, 255, 255, 0.14); border: none; border-radius: 50%; cursor: pointer;
    width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center;
    flex: none; margin: 0 0.5rem;
  }
  .nav:hover { background: rgba(255, 255, 255, 0.26); }
  .nav:disabled { opacity: 0.3; cursor: default; }
`;

export interface DriveGalleryModalProps {
  title: string;
  /** Asks for a page of the gallery. The two callers authorize differently and answer the same shape. */
  load(from: number, limit: number): Promise<DriveGalleryPage>;
  onClose(): void;
}

export const DriveGalleryModal = (props: DriveGalleryModalProps) => {
  const [page, setPage] = useState<DriveGalleryPage>();
  const [items, setItems] = useState<DriveGalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<number>();
  // One re-fetch when a thumbnail url has expired under a page left open; more than one would spin
  // forever on a picture that is genuinely gone.
  const refetched = useRef(false);

  const fetchFrom = useCallback(async (from: number) => {
    setLoading(true);
    try {
      const next = await props.load(from, PAGE);
      setPage(next);
      setItems((previous) => (from ? [...previous, ...next.items] : next.items));
      setFailed(false);
    }
    catch (_) {
      setFailed(true);
    }
    finally {
      setLoading(false);
    }
  }, [props.load]);

  useEffect(() => { fetchFrom(0); }, [fetchFrom]);

  // While the index is still running the archive's pictures appear as they are drawn, so the page asks
  // again rather than making someone close the modal and reopen it.
  useEffect(() => {
    if (page?.status !== 'pending') return undefined;
    const timer = setTimeout(() => fetchFrom(0), POLL_MS);
    return () => clearTimeout(timer);
  }, [page?.status, page?.total, fetchFrom]);

  const show = (index: number) => setOpen(index);
  const step = (by: number) => setOpen((at) => {
    if (at === undefined) return at;
    const next = at + by;
    return next >= 0 && next < items.length ? next : at;
  });

  useEffect(() => {
    if (open === undefined) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
      // Esc closes the lightbox and leaves the gallery standing; the modal's own Esc would close both.
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(undefined); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, items.length]);

  const onThumbError = () => {
    if (refetched.current) return;
    refetched.current = true;
    fetchFrom(0);
  };

  const shot = open === undefined ? undefined : items[open];

  return (
    <Modal styled={DriveGalleryModalStyled} title={props.title} onClose={props.onClose}>
      {!items.length && (loading || page?.status === 'pending') && (
        <div className="gallery-note">{strings.GalleryPreparing()}</div>
      )}
      {!items.length && !loading && (failed || page?.status === 'failed') && (
        <div className="gallery-note">{strings.GalleryFailed()}</div>
      )}
      {!items.length && !loading && page?.status === 'unsupported' && (
        <div className="gallery-note">{strings.GalleryUnsupported()}</div>
      )}
      {!items.length && !loading && page?.status === 'done' && (
        <div className="gallery-note">{strings.GalleryEmpty()}</div>
      )}

      {!!items.length && (
        <div className="gallery">
          {items.map((item, index) => (
            <button
              type="button"
              key={`${item.index}-${item.name}`}
              className="shot"
              onClick={() => show(index)}
            >
              <span className="art">
                {item.thumbUrl
                  ? <img src={item.thumbUrl} alt={item.name} loading="lazy" onError={onThumbError} />
                  : <Icon icon="image" size={40} fill={css.grey5} />}
              </span>
              <span className="cap">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="gallery-foot">
        {!!page?.total && <span>{strings.GalleryOf(items.length, page.total)}</span>}
        {!!page?.others && <span>{strings.GalleryOtherFiles(page.others)}</span>}
        {page?.archiveUrl && (
          <a href={page.archiveUrl} target="_blank" rel="noreferrer">{strings.DownloadArchive()}</a>
        )}
        {!!page && page.total > items.length && (
          <Button className="secondary spacer" disabled={loading} onClick={() => fetchFrom(items.length)}>
            {strings.LoadMore()}
          </Button>
        )}
      </div>

      {shot && (
        <LightboxStyled>
          <div className="bar">
            <span className="name">{shot.name}</span>
            <span>{formatBytes(shot.size)}</span>
            <span className="spacer">{strings.GalleryOf(open! + 1, page?.total || items.length)}</span>
            <a href={shot.url} target="_blank" rel="noreferrer" download={shot.name}>{strings.Download()}</a>
            <button type="button" className="nav" aria-label={strings.Close()} onClick={() => setOpen(undefined)}>
              <Icon icon="close" size={20} fill={css.white} />
            </button>
          </div>
          <div className="shot-area">
            <button type="button" className="nav" aria-label={strings.Previous()} disabled={open === 0} onClick={() => step(-1)}>
              <Icon icon="chevronLeft" size={22} fill={css.white} />
            </button>
            <img src={shot.url} alt={shot.name} />
            <button
              type="button"
              className="nav"
              aria-label={strings.Next()}
              disabled={open === items.length - 1}
              onClick={() => step(1)}
            >
              <Icon icon="chevronRight" size={22} fill={css.white} />
            </button>
          </div>
        </LightboxStyled>
      )}
    </Modal>
  );
};
